import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, MapPin } from "lucide-react";
import { MediaGallery, DocumentList } from "@/components/common/Media";
import { Spinner, EmptyState, Badge } from "@/components/common/Primitives";
import { formatDate, isUpcoming } from "@/lib/format";
import { isInitialLoading, useEvent, useForum, useUnit } from "@/hooks/useSiteQueries";

export default function EventDetailPage() {
  const { slug } = useParams();
  const eventQuery = useEvent(slug);
  const forumQuery = useForum(eventQuery.data?.forum_slug);
  const unitQuery = useUnit(eventQuery.data?.unit_slug);

  if (isInitialLoading([eventQuery])) return <Spinner className="py-32" />;

  const event = eventQuery.data;
  if (!event) {
    return (
      <div className="container-bcs py-24">
        <EmptyState title="Event not found" />
      </div>
    );
  }

  const forum = forumQuery.data;
  const unit = unitQuery.data;

  return (
    <div data-testid={`event-detail-${slug}`}>
      <section className="relative bg-maroon text-white overflow-hidden">
        <img src={event.cover_image} alt={event.title} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-maroon via-maroon/80 to-maroon/50" />
        <div className="relative container-bcs py-20 sm:py-28">
          <Link to="/events" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors mb-6">
            <ArrowLeft size={15} /> All events
          </Link>
          <Badge tone={isUpcoming(event.event_date) ? "green" : "gray"}>
            {isUpcoming(event.event_date) ? "Upcoming" : "Past event"}
          </Badge>
          <h1 className="mt-4 heading-xl !text-white max-w-3xl">{event.title}</h1>
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/85">
            <span className="flex items-center gap-2"><Calendar size={16} className="text-gold-light" /> {formatDate(event.event_date)}</span>
            {event.event_time && <span className="flex items-center gap-2"><Clock size={16} className="text-gold-light" /> {event.event_time}</span>}
            <span className="flex items-center gap-2"><MapPin size={16} className="text-gold-light" /> {event.venue}</span>
          </div>
        </div>
      </section>

      <section className="container-bcs py-14 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-12">
            <div>
              <h2 className="heading-md mb-4">About this event</h2>
              <p className="text-ink-soft leading-relaxed whitespace-pre-line">{event.description}</p>
            </div>
            <MediaGallery images={event.gallery || []} title="Event gallery" />
            <DocumentList documents={event.documents || []} title="Event downloads" />
          </div>
          <aside className="space-y-5">
            {forum && (
              <Link to={`/our-work/${forum.slug}`} className="card-bcs p-6 block group" data-testid="event-related-forum">
                <p className="eyebrow !text-[10px]">Organised by</p>
                <p className="mt-2 font-serif text-lg font-semibold text-ink group-hover:text-maroon transition-colors">
                  {forum.name}
                </p>
                <p className="mt-1 text-sm text-ink-muted">{forum.tagline}</p>
              </Link>
            )}
            {unit && (
              <Link to={`/units/${unit.slug}`} className="card-bcs p-6 block group" data-testid="event-related-unit">
                <p className="eyebrow !text-[10px]">Host unit</p>
                <p className="mt-2 font-serif text-lg font-semibold text-ink group-hover:text-maroon transition-colors">
                  {unit.name}
                </p>
                <p className="mt-1 text-sm text-ink-muted">{unit.deanery} Deanery · {unit.area}</p>
              </Link>
            )}
            <div className="card-bcs p-6 bg-cream-alt">
              <p className="font-serif text-lg font-semibold text-ink">Questions about this event?</p>
              <p className="mt-1.5 text-sm text-ink-soft">Contact the BCS office and we'll connect you with the organisers.</p>
              <Link to="/contact" className="btn-primary mt-4 w-full" data-testid="event-contact-btn">Contact us</Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
